import { Component, Watch } from "vue-property-decorator";
import { OrgModule } from "@/store/modules/org";
import { UserModule } from "@/store/modules/user";
import DropdownsState, {
  DropdownStateEnum,
} from "@/mixins/DropdownsState.mixin";
import {
  Button,
  ButtonShapeEnum,
  ButtonSizeEnum,
  ButtonThemeEnum,
} from "../shared/Button.component";
import { getOrgNames } from "@/models/org";
import downArrowBlack from "@/assets/img/down-chevron-dark.svg";
import avatarLogoBlack from "@/assets/img/avatar-black.svg";
import { userDataLoader } from "@/utils/auth-service";
import plusIconBlack from "@/assets/img/plus-icon.svg";
import plusIconWhite from "@/assets/img/plus-icon-light.svg";
import styles from "./NavbarDropdownDesktop.component.module.scss";
import * as tsx from "vue-tsx-support";
import {
  BNavItemDropdown,
  BNavText,
  BDropdownItem,
  BDropdownDivider,
} from "@/lib/typed-bootstrap";

@Component
export default class NavbarDropdownDesktop extends DropdownsState {
  _tsx!: tsx.DeclareProps<{}> & tsx.DeclareOnEvents<{}>;

  desktopToggleId = "desktopNavDropdown";

  userOrgs: { id: string; name: string }[] = [];

  avatarSrc = avatarLogoBlack;

  downArrowSrc = downArrowBlack;

  plusIconSrc = plusIconBlack;

  plusIconHoverSrc = plusIconWhite;

  get buttonConfig() {
    return {
      shape: ButtonShapeEnum.square,
      theme: ButtonThemeEnum.naked,
      size: ButtonSizeEnum.sm,
    };
  }

  get userOrgIds() {
    return Object.keys(UserModule.roles);
  }

  get isShown() {
    return this.dropDownState[this.desktopToggleId] === DropdownStateEnum.shown;
  }

  get orgId(): string {
    return OrgModule.id;
  }

  get orgName(): string {
    return OrgModule.name ?? "Unknown organization";
  }

  get userEmail(): string {
    return UserModule.email;
  }

  get userIsAuthenticated() {
    return UserModule.isAuthenticated;
  }

  @Watch("userOrgIds", { immediate: true })
  async getOrgNames() {
    if (this.userOrgIds.length > 0) {
      this.userOrgs = await getOrgNames(this.userOrgIds);
    } else {
      this.userOrgs = [];
    }
  }

  async onSelectOrg(orgId: string) {
    await UserModule.onUserAction(async () => {
      userDataLoader.switchOrg();
    });
    this.$router
      .push({
        name: "org-api-dashboard",
        params: {
          orgId,
        },
      })
      .catch(() => undefined);
  }

  onCreateOrg() {
    this.$router.push({ name: "workspace-setup" });
  }

  logout() {
    this.$authManager.logout();
  }

  @Watch("userIsAuthenticated")
  async onUserAuthChange(): Promise<void> {
    if (!this.userIsAuthenticated) {
      this.hideDropdown(this.desktopToggleId);
    }
  }

  render() {
    return (
      <BNavItemDropdown
        class={styles.dropdown}
        menuClass={styles.dropdownMenu}
        onShown={() => this.showDropdown(this.desktopToggleId)}
        onHidden={() => this.hideDropdown(this.desktopToggleId)}
        data-testid="navbar-dropdown-desktop"
        noCaret
        right
        scopedSlots={{
          "button-content": () => (
            <div class={styles.user}>
              <img src={this.avatarSrc} class={styles.avatar} />
              <div class={styles.userEmail}>
                {this.userEmail}
                <span class={styles.userOrg} data-testid="current-org-name">
                  {this.orgName}
                </span>
              </div>
              <img
                class={[styles.userDownIcon, this.isShown && styles.rotate]
                  .filter(Boolean)
                  .join(" ")}
                src={this.downArrowSrc}
              />
            </div>
          ),
        }}
      >
        <BNavText class={styles.dropdownSectionHeader}>
          {this.$t("nav_dropdown_workspaces_header").toString()}
        </BNavText>
        {this.userOrgs.map((org) => (
          <BDropdownItem
            key={org.id}
            onClick={() => this.onSelectOrg(org.id)}
            class={[styles.dropdownItem, styles.dropdownOrgItem].join(" ")}
            data-testid={`org-dropdown-${org.id}`}
          >
            {org.name}
          </BDropdownItem>
        ))}

        <Button
          data-testid="create-workspace"
          class={[
            styles.dropdownItem,
            styles.dropdownOrgItem,
            styles.actionButton,
          ].join(" ")}
          shape={this.buttonConfig.shape}
          theme={this.buttonConfig.theme}
          size={this.buttonConfig.size}
          onClick={this.onCreateOrg}
          scopedSlots={{
            content: ({ isHovered }: { isHovered: boolean }) => (
              <div>
                <img
                  src={isHovered ? this.plusIconHoverSrc : this.plusIconSrc}
                />
                <span class={styles.createWorkspaceCta}>
                  {this.$t("form_cta_create_workspace").toString()}
                </span>
              </div>
            ),
          }}
        />
        <BDropdownDivider class={styles.divider} />
        <BDropdownItem
          class={styles.dropdownItem}
          to={{ name: "account-settings" }}
        >
          {this.$t("account_settings_title").toString()}
        </BDropdownItem>
        <BDropdownDivider class={styles.divider} />
        <BDropdownItem
          data-testid="logout"
          onClick={this.logout}
          class={styles.dropdownItem}
        >
          {this.$t("logout_cta_submit").toString()}
        </BDropdownItem>
      </BNavItemDropdown>
    );
  }
}
